import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { AssessmentResultType } from "../../pages/Assessment";
import { CheckCircle, AlertCircle, HelpCircle, MessageSquare } from "lucide-react";

type AssessmentResultsProps = {
  result: AssessmentResultType;
};

const AssessmentResults = ({ result }: AssessmentResultsProps) => {
  const { score, severityLevel, recommendation } = result;
  
  const getSeverityColor = () => {
    switch (severityLevel) {
      case "minimal": return "text-green-600";
      case "mild": return "text-blue-600";
      case "moderate": return "text-yellow-600";
      case "moderately severe": return "text-orange-600";
      case "severe": return "text-red-600";
      default: return "text-gray-600";
    }
  };

  const getResultIcon = () => {
    switch (severityLevel) {
      case "minimal": 
      case "mild": 
        return <CheckCircle className="h-16 w-16 text-green-500" />;
      case "moderate": 
        return <HelpCircle className="h-16 w-16 text-yellow-500" />;
      case "moderately severe":
      case "severe": 
        return <AlertCircle className="h-16 w-16 text-red-500" />;
      default: 
        return <HelpCircle className="h-16 w-16 text-gray-500" />;
    }
  };

  const getNextStepsContent = () => {
    switch (severityLevel) {
      case "minimal":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Recommended Actions</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Continue your current self-care practices</li>
              <li>Engage in regular physical activity</li>
              <li>Maintain healthy sleep patterns</li>
              <li>Practice mindfulness and relaxation techniques when needed</li>
            </ul>
          </div>
        );
      case "mild":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Recommended Actions</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Consider self-help resources like books or apps</li>
              <li>Increase physical activity and time outdoors</li>
              <li>Practice stress-reduction techniques</li>
              <li>Talk to trusted friends or family about how you're feeling</li>
              <li>If symptoms persist for more than two weeks, consider professional support</li>
            </ul>
          </div>
        );
      default:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Recommended Actions</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Connect with a culturally-competent therapist through our platform</li>
              <li>Talk to your primary care provider about your mental health</li>
              <li>Continue self-care practices like physical activity and mindfulness</li>
              <li>Reach out to supportive friends and family</li>
              <li>Practice self-compassion during this challenging time</li>
            </ul>
          </div>
        );
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-4 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Your Assessment Results</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Thank you for completing the mental health assessment.
        </p>
      </div>

      <Card className="border-2 border-violet-100">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {getResultIcon()}
          </div>
          <CardTitle className="text-2xl mb-2">
            <span className={getSeverityColor()}>
              {severityLevel.charAt(0).toUpperCase() + severityLevel.slice(1)}
            </span> level of distress
          </CardTitle>
          <CardDescription className="text-base">
            Score: {score} out of 30
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-violet-50 p-4 rounded-lg border border-violet-100">
            <p className="text-lg">{recommendation}</p>
          </div>
          
          {getNextStepsContent()}
          
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h3 className="text-lg font-medium mb-2">Important Note</h3>
            <p>
              This assessment is a screening tool and not a diagnostic instrument. Your results are meant to give you an indication of your current level of distress and whether professional support might be beneficial.
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col sm:flex-row gap-4 pt-4">
          <Link to="/browse-therapists" className="w-full sm:w-auto">
            <Button className="w-full bg-gradient-to-r from-violet-600 to-indigo-600">
              <MessageSquare className="mr-2 h-4 w-4" /> Find a Therapist
            </Button>
          </Link>
          <Link to="/" className="w-full sm:w-auto">
            <Button variant="outline" className="w-full">
              Return Home
            </Button>
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
};

export default AssessmentResults;
